<template>
	<app-header-teleport>
		<div class="flex flex-1 items-center gap-4">
			<app-title :title="$t('dynamic_reports')" />
			<app-advanced-filter
				v-if="selectedReport !== null"
				@onFilter="onFilter"
				@onClear="onClear"
				:loading="isLoading"
			>
				<app-advanced-date-filter v-model="filters" :start-date="'startDate'" :end-date="'endDate'" />
				<app-city-select v-if="filters.cityId !== undefined" id="cityFilter" v-model="filters.cityId" />
				<app-input
					v-if="filters.orderId !== undefined"
					type="text"
					name="Name"
					v-model="filters.orderId"
					:placeholder="$t('order')"
				/>
				<div v-for="(filter, index) in dynamicFilters" :key="index">
					<app-select
						v-if="filter.type === 'app-select'"
						class="w-full"
						:placeholder="filter.placeholder"
						v-model="filters[filter.code]"
						:options="filter.options"
					/>
					<app-input
						v-if="filter.type === 'app-input'"
						:name="filter.code"
						:placeholder="filter.placeholder"
						v-model="filters[filter.code]"
						class="w-full"
					/>
					<div v-if="filter.type === 'app-switch'" class="flex gap-2 items-center mb-4">
						<span class="text-13 text-secondary">{{ filter.placeholder }}</span>
						<app-switch
							:on-label="filter.onLabel"
							:off-label="filter.offLabel"
							:model-value="filters[filter.code]"
							:true-value="filter.trueValue"
							:false-value="filter.falseValue"
							@change="filters[filter.code] = $event"
						/>
					</div>
				</div>
			</app-advanced-filter>
			<span class="text-2xl font-semibold">{{ reportName }}</span>
		</div>
	</app-header-teleport>
	<div v-if="reports.length" class="flex flex-1 flex-row mt-4 gap-4">
		<app-section v-if="!hiddenList" class="flex w-1/6 flex-1 table-app-section">
			<div class="flex flex-col items-start justify-center">
				<slot name="searchFilter">
					<app-input
						v-model="computedSearch"
						name="search"
						:placeholder="$t('search')"
						icon="search"
						:debounce-interval="800"
						class="w-full"
						@update:modelValue="_onSearchInput"
					/>
				</slot>
				<ul class="w-full max-h-screen overflow-y-auto mb-3">
					<li v-for="section in filteredReports" :key="section.id" class="mb-1">
						<div
							@click="toggleSection(section.id)"
							class="flex items-center justify-between cursor-pointer font-semibold text-gray-700 px-4 py-2 bg-gray-100"
						>
							<span>{{ section.category }}</span>
							<app-icon
								:icon="collapsedSections.includes(section.id) ? 'sort-down' : 'sort-up'"
								class="text-gray-700"
							/>
						</div>
						<ul v-show="!collapsedSections.includes(section.id)">
							<li
								v-for="report in section.dynamicReports"
								:key="report.id"
								@click="onReportSelect(report)"
								:class="[
									'cursor-pointer p-1 px-4 light-blue border-gray-300 border-b-1',
									{ 'bg-gray-200': isSelectedReport(report) },
								]"
							>
								{{ report.reportTitle }}
							</li>
						</ul>
					</li>
				</ul>
			</div>
		</app-section>
		<div
			class="flex items-center justify-center bg-primary cursor-pointer h-6 w-6 absolute top-0 left-0 custom-margin-top z-1000"
			@click="hiddenList = !hiddenList"
		>
			<app-icon :icon="hiddenList ? 'right' : 'left'" class="text-white" />
		</div>
		<div :class="['flex flex-col w-5/6', hiddenList ? 'w-full' : '']" style="height: fit-content;">
			<div v-for="section in sectionsData" :key="section.id">
				<app-section v-if="section.type === 'TABLE'" class="flex flex-col flex-1 table-app-section mb-4">
					<dynamic-app-table
						class="w-full"
						:rows="section.rows || []"
						:columns="Object.values(section.columns)"
						:footer="section?.footer || []"
						:reportName="reportName"
					>
						<template v-slot:searchFilter>
							<app-input
								class="flex-1"
								name="search"
								v-model="filters.userSearch"
								:placeholder="$t('search')"
								:icon="searching ? 'spin-loading' : 'search'"
								:debounce-interval="600"
								@update:model-value="_onSearchInputReport"
							/>
						</template>
					</dynamic-app-table>
					<app-pagination
						v-if="section.rows"
						:filters="filters"
						@prevPage="prevPage"
						@nextPage="nextPage"
						@limitChange="changeLimit"
						@loadMore="loadMore"
						:length="section.rows.length"
						:loading="isLoading || refreshing"
					/>
				</app-section>
				<div v-if="section.type === 'KPI' && !!section?.kpis?.length" class="flex flex-col flex-1 mb-4">
					<h3 v-if="section.title" class="text-lg font-semibold mb-3 px-1">{{ section.title }}</h3>
					<div
						v-for="(group, gIdx) in kpiGroups(section)"
						:key="gIdx"
						class="flex flex-1 flex-wrap items-stretch mb-2 last:mb-0"
					>
						<app-card
							v-for="kpi in group"
							:key="kpi.label"
							@click="onKpiClick(kpi)"
							:class="[
								'flex-1 min-w-[160px] kpi-card mb-2 cursor-pointer border-1',
								isActiveKpi(kpi) ? 'border-primary' : 'border-transparent',
							]"
							:title="kpiTitle(kpi)"
							:text="kpi.value"
							:propValue="kpi.label"
						/>
					</div>
				</div>
				<app-section
					v-if="section.type === 'CHART_BAR' && section.horizontalLabels"
					class="flex flex-col flex-1 mb-4"
				>
					<app-bar-chart
						name="Chart Bar"
						:labels="section.horizontalLabels"
						class="h-80"
						:series="section.series || []"
					/>
				</app-section>
				<app-section
					v-if="section.type === 'CHART_LINE' && section.horizontalLabels"
					class="flex flex-col flex-1 mb-4"
				>
					<app-line-chart :labels="section.horizontalLabels" :series="section.series || []" height="400" />
				</app-section>
				<app-section
					v-if="section.type === 'PIE' && section.pieLabels"
					class="flex flex-col items-center justify-center flex-1 mb-4"
				>
					<app-pie-chart
						:labels="section.pieLabels"
						:series="section.pieResult || []"
						width="600"
						height="300"
					/>
				</app-section>
			</div>
			<app-section
				v-if="selectedReport === null"
				class="flex items-center justify-center text-xl gap-2 py-4 w-full h-24"
			>
				<app-icon icon="doc" class="text-gray-600" />
				<span class="text-gray-600">{{ $t('noSelectedReport') }}</span>
			</app-section>
		</div>
	</div>
	<app-section v-if="!reports.length" class="flex items-center justify-center text-xl gap-2 py-4 w-full h-24">
		<app-icon icon="user" class="text-gray-600" />
		<span class="text-gray-600">{{ $t('noReportPermission') }}</span>
	</app-section>
</template>

<script>
	import ReportService from '@/services/ReportService';
	import AppSection from '@/components/app/AppSection.vue';
	import DynamicAppTable from '@/components/app/DynamicAppTable.vue';
	import AppAdvancedFilter from '@/components/app/AppAdvancedFilter.vue';
	import AppAdvancedDateFilter from '@/components/app/AppAdvancedDateFilter.vue';
	import AppPagination from '@/components/app/AppPagination.vue';
	import { debounce } from 'debounce';
	import AppInput from '@/components/app/AppInput.vue';
	import AppIcon from '@/components/app/AppIcon.vue';
	import AppBarChart from '@/components/app/AppBarChart.vue';
	import AppCitySelect from '@/components/app/AppCitySelect.vue';
	import { mapGetters } from 'vuex';
	import AppPieChart from '@/components/app/AppPieChart.vue';
	import AppSwitch from '@/components/app/AppSwitch.vue';
	import AppLineChart from '@/components/app/AppLineChart.vue';
	import AppCard from '@/components/app/AppCard.vue';

	export default {
		name: 'DynamicReports',
		components: {
			AppLineChart,
			AppSwitch,
			AppPieChart,
			AppCitySelect,
			AppBarChart,
			AppIcon,
			AppInput,
			AppPagination,
			AppAdvancedDateFilter,
			AppAdvancedFilter,
			DynamicAppTable,
			AppSection,
			AppCard,
		},
		data() {
			return {
				defaultFilter: {},
				filters: {},
				searchFilters: {},
				sectionsData: [],
				reports: [],
				selectedReport: null,
				reportName: '',
				collapsedSections: [],
				isLoading: false,
				refreshing: false,
				searching: false,
				isFirstSelection: false,
				computedSearch: '',
				hiddenList: false,
				dynamicFilters: [],
			};
		},
		computed: {
			...mapGetters({
				selectedRestaurants: 'selectedRestaurants',
			}),
			businessIds() {
				return this.selectedRestaurants.length > 1
					? this.selectedRestaurants.join('-')
					: this.selectedRestaurants[0];
			},
			filteredReports() {
				return this.reports
					.map((report) => {
						const filteredOptions = Array.isArray(report.dynamicReports)
							? report.dynamicReports.filter((option) =>
									option.reportTitle.toLowerCase().includes(this.computedSearch.toLowerCase())
							  )
							: [];
						return {
							...report,
							dynamicReports: filteredOptions,
						};
					})
					.filter((report) => report.dynamicReports.length > 0);
			},
			isSelectedReport() {
				return (report) => report.id === this.selectedReport;
			},
		},
		methods: {
			kpiFilterCode(kpi) {
				const map = {
					Delivered: 'DELIVERED',
					Rejected: 'REJECTED',
					Refunded: 'REFUNDED',
					'Service Fee': 'SERVICE_FEE',
					'Refund Baboon': 'REFUND_BABOON',
					'Refund Business': 'REFUND_BUSINESS',
					Marketing: 'MARKETING_BABOON',
				};
				return map[kpi.label] || null;
			},
			isActiveKpi(kpi) {
				return this.filters.kpiFilter === this.kpiFilterCode(kpi);
			},
			onKpiClick(kpi) {
				const code = this.kpiFilterCode(kpi);
				if (!code) return;
				const next = this.filters.kpiFilter === code ? null : code;
				this.filters.kpiFilter = next;
				this.filters.offset = 0;
				this.searchFilters.kpiFilter = next;
				this.searchFilters.offset = 0;
				this.getDynamicReportById();
			},
			kpiTitle(kpi) {
				const label = kpi.label || '';
				if (kpi.caption) {
					return `${label}<br><span class="text-12 text-secondary italic">${kpi.caption}</span>`;
				}
				return label;
			},
			kpiGroups(section) {
				const kpis = section.kpis || [];
				const groups = {};
				const order = [];
				for (const kpi of kpis) {
					const key = kpi.group || '_default';
					if (!(key in groups)) {
						groups[key] = [];
						order.push(key);
					}
					groups[key].push(kpi);
				}
				return order.map((k) => groups[k]);
			},
			toggleSection(index) {
				if (this.collapsedSections.includes(index)) {
					this.collapsedSections = this.collapsedSections.filter((i) => i !== index);
				} else {
					this.collapsedSections.push(index);
				}
			},
			getDynamicReports() {
				this.emitter.emit('isLoading', true);
				ReportService.getDynamicReportsCategories()
					.then((response) => {
						this.reports = response;
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.collapsedSections = this.reports.map((report) => report.id);
						if (this.$route.query.reportId) {
							this.selectedReport = Number(this.$route.query.reportId);
							this.getDynamicReportById();
						} else {
							this.emitter.emit('isLoading', false);
						}
					});
			},
			getDynamicReportById(propToLoad = 'isLoading', loadMore = false) {
				this[propToLoad] = true;
				if (propToLoad === 'isLoading') {
					this.emitter.emit('isLoading', true);
				}
				const report = this.reports
					.flatMap((section) => section.dynamicReports)
					.find((report) => report?.id === this.selectedReport);
				this.reportName = report.reportTitle || '';
				this.dynamicFilters = [];
				if (report) {
					if (this.isFirstSelection && report.filters.length) {
						report.filters.forEach((filter) => {
							this.filters[filter.code] = filter.defaultValue;
							this.searchFilters[filter.code] = filter.defaultValue;
							this.defaultFilter[filter.code] = filter.defaultValue;
							if (filter.type) {
								this.dynamicFilters.push(filter);
							}
						});
						this.isFirstSelection = false;
					} else {
						report.filters.forEach((filter) => {
							if (filter.type) {
								this.dynamicFilters.push(filter);
							}
						});
					}
					if (!loadMore) {
						this.$router.push({ query: this.searchFilters });
					}
					const isSingleRestaurant = this.selectedRestaurants.length === 1;
					this.filters.businessId = isSingleRestaurant ? this.businessIds : null;
					this.searchFilters.businessId = isSingleRestaurant ? this.businessIds : null;
					this.filters.businessIDs = isSingleRestaurant ? null : this.businessIds;
					this.searchFilters.businessIDs = isSingleRestaurant ? null : this.businessIds;
					ReportService.getDynamicReportById(report.id, this.searchFilters)
						.then((response) => {
							response = Array.isArray(response) ? response : [response];
							if (loadMore) {
								const existingTables = this.sectionsData.filter((section) => section.type === 'TABLE');
								const responseTables = response.filter((section) => section.type === 'TABLE');
								let appendedAny = false;
								responseTables.forEach((responseTable, index) => {
									const existingTable = existingTables[index];
									if (!existingTable || !responseTable.rows?.length) return;
									const isAggregateDuplicate =
										existingTable.rows.length > 0 &&
										JSON.stringify(responseTable.rows[0]) ===
											JSON.stringify(existingTable.rows[0]);
									if (isAggregateDuplicate) return;
									existingTable.rows = existingTable.rows.concat(responseTable.rows);
									if (existingTable.footer && responseTable.footer) {
										existingTable.footer = this.sumFooterValues(
											existingTable.footer,
											responseTable.footer
										);
									} else if (responseTable.footer) {
										existingTable.footer = responseTable.footer;
									}
									appendedAny = true;
								});
								if (!appendedAny) this.$notificationService.info('No more data to load');
							} else {
								this.sectionsData = response;
							}
						})
						.catch((error) => {
							this.$notificationService.error(error);
						})
						.finally(() => {
							this.emitter.emit('isLoading', false);
							this[propToLoad] = false;
						});
				} else {
					this.emitter.emit('isLoading', false);
					this[propToLoad] = false;
				}
			},
			_onSearchInput(value) {
				this.computedSearch = value;
			},
			_onSearchInputReport(value) {
				this.searchFilters.userSearch = value;
				this.getDynamicReportById('searching');
			},
			onReportSelect(report) {
				this.selectedReport = report.id;
				this.isFirstSelection = true;
				this.filters = { reportId: this.selectedReport };
				this.searchFilters = { reportId: this.selectedReport };
				this.getDynamicReportById();
			},
			prevPage() {
				this.searchFilters.offset = Math.max(
					0,
					Number(this.searchFilters.offset) - Number(this.searchFilters.limit)
				);
				this.getDynamicReportById('refreshing');
			},
			nextPage() {
				this.searchFilters.offset = Number(this.searchFilters.offset) + Number(this.searchFilters.limit);
				this.getDynamicReportById('refreshing');
			},
			onFilter() {
				this.searchFilters = this.filters;
				this.getDynamicReportById();
			},
			onClear() {
				this.searchFilters = { reportId: this.searchFilters.reportId, ...this.defaultFilter };
				this.filters = { reportId: this.filters.reportId, ...this.defaultFilter };
				this.getDynamicReportById();
			},
			changeLimit(value) {
				this.searchFilters = {
					...this.searchFilters,
					limit: value,
					offset: 0,
				};
				this.filters = this.searchFilters;
				this.getDynamicReportById('refreshing');
			},
			loadMore() {
				this.searchFilters.offset = Number(this.searchFilters.offset) + Number(this.searchFilters.limit);
				this.filters = this.searchFilters;
				this.getDynamicReportById('refreshing', true);
			},
			sumFooterValues(currentFooter, newFooter) {
				return currentFooter.map((footerItem, index) => {
					if (index === 0 || footerItem.value === '' || !footerItem.value) {
						return footerItem;
					}
					const newFooterItem = newFooter[index];
					if (!newFooterItem || newFooterItem.value === '' || !newFooterItem.value) {
						return footerItem;
					}
					const currentValue = Number(String(footerItem.value).replace(/,/g, ''));
					const newValue = Number(String(newFooterItem.value).replace(/,/g, ''));
					const sum = currentValue + newValue;
					const formattedSum = sum.toLocaleString('en-US');

					return {
						...footerItem,
						value: formattedSum,
					};
				});
			},
		},
		mounted() {
			if (Object.keys(this.$route.query).length) {
				this.filters = Object.assign(this.filters, this.$route.query);
				this.searchFilters = Object.assign(this.searchFilters, this.$route.query);
			}
			this.getDynamicReports();
		},
		watch: {
			computedSearch() {
				if (this.computedSearch === '') {
					this.collapsedSections = this.filteredReports.map((report) => report.id);
				} else {
					this.collapsedSections = [];
				}
			},
			selectedRestaurants: debounce(function() {
				if (!this.$store.getters.token) return;
				this.getDynamicReportById();
			}, 1500),
		},
	};
</script>

<style scoped>
	.light-blue:hover {
		background: rgba(66, 153, 225, 0.2);
	}
	.table-app-section {
		margin-top: 0 !important;
		height: fit-content;
	}
	.custom-margin-top {
		margin-top: 51px !important;
	}
</style>
